import { httpRequest } from '@/services/httpClient'

export type TableColumnDef = {
  fieldName: string
  dataType: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'textarea'
  showInTable: boolean
  label: string
  showInForm: boolean
  required: boolean
  width: number
  sortOrder: number
}

export type FormMasterItem = {
  id: number
  tenantId: number
  entityName: string
  displayName: string
  description: string | null
  parentMenu: string
  menuName: string
  tableColumns: string
  exampleData: string
  exampleHeaders: string
  listViewType: string
  hasImport: boolean
  hasExport: boolean
  filterType: string
  hasRefresh: boolean
  hasCreate: boolean
  pageSize: number
  backendRoute: string
  frontendRoute: string
  responseFormat: string
  responseDataPath: string | null
  responsePaginationPath: string | null
  responseNotes: string | null
  createdAt: string
  updatedAt: string | null
}

export type FormMasterListResponse = {
  items: FormMasterItem[]
  totalCount: number
  page: number
  pageSize: number
}

export type FormMasterListParams = {
  search?: string
  entityName?: string
  page?: number
  pageSize?: number
  sortBy?: 'entityName' | 'displayName' | 'parentMenu' | 'updatedAt'
  sortDir?: 'asc' | 'desc'
}

export type FormMasterCreateUpdatePayload = {
  entityName: string
  displayName: string
  description?: string
  parentMenu: string
  menuName: string
  tableColumns: string
  exampleData?: string
  exampleHeaders?: string
  listViewType?: string
  hasImport?: boolean
  hasExport?: boolean
  filterType?: string
  hasRefresh?: boolean
  hasCreate?: boolean
  pageSize?: number
  backendRoute: string
  frontendRoute: string
  responseFormat?: string
  responseDataPath?: string
  responsePaginationPath?: string
  responseNotes?: string
}

export type FormMasterCreateResult = {
  id: number
  entityName: string
  displayName: string
}

export const PARENT_MENU_OPTIONS = [
  { value: 'system', label: 'formMaster.parentMenuOptions.system' },
  { value: 'admin', label: 'formMaster.parentMenuOptions.admin' },
  { value: 'data', label: 'formMaster.parentMenuOptions.data' },
  { value: 'reports', label: 'formMaster.parentMenuOptions.reports' },
  { value: 'developer', label: 'formMaster.parentMenuOptions.developer' },
] as const

export const DATA_TYPE_OPTIONS = [
  { value: 'text', label: 'formMaster.dataTypes.text' },
  { value: 'number', label: 'formMaster.dataTypes.number' },
  { value: 'date', label: 'formMaster.dataTypes.date' },
  { value: 'boolean', label: 'formMaster.dataTypes.boolean' },
  { value: 'select', label: 'formMaster.dataTypes.select' },
  { value: 'textarea', label: 'formMaster.dataTypes.textarea' },
] as const

export const LIST_VIEW_OPTIONS = [
  { value: 'table', label: 'formMaster.listViewOptions.table' },
  { value: 'card', label: 'formMaster.listViewOptions.card' },
] as const

export const formMasterApi = {
  list: (params: FormMasterListParams) =>
    httpRequest<FormMasterListResponse>('/v1/form-master-definitions', {
      method: 'GET',
      query: {
        search: params.search?.trim() || undefined,
        entityName: params.entityName?.trim() || undefined,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 10,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      },
    }),

  getById: (id: number) =>
    httpRequest<FormMasterItem>(`/v1/form-master-definitions/${id}`, { method: 'GET' }),

  create: (payload: FormMasterCreateUpdatePayload) =>
    httpRequest<FormMasterCreateResult>('/v1/form-master-definitions', {
      method: 'POST',
      body: payload,
    }),

  update: (id: number, payload: FormMasterCreateUpdatePayload) =>
    httpRequest<FormMasterItem>(`/v1/form-master-definitions/${id}`, {
      method: 'PUT',
      body: payload,
    }),

  delete: (id: number) =>
    httpRequest<undefined>(`/v1/form-master-definitions/${id}`, { method: 'DELETE' }),
}
